import React, {ReactNode, useState, useEffect, useRef} from 'react'

interface TooltipProps {
  message: string
  position?: 'top' | 'bottom' | 'left' | 'right'
  children: ReactNode
  customStyles?: React.CSSProperties;
  MenuallyPosition?: boolean
}

const CustomTooltip: React.FC<TooltipProps> = ({
  message,
  position = 'top',
  children,
  customStyles = {},
  MenuallyPosition = false,
}) => {
  const [showTooltip, setShowTooltip] = useState(false)
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({})
  const [arrowStyle, setArrowStyle] = useState<React.CSSProperties>({})
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (MenuallyPosition) {
      setTooltipStyle(customStyles)
      return
    }

    if (showTooltip && containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect()
      const tooltipBox = document.querySelector('.tooltip-box') as HTMLDivElement

      if (tooltipBox) {
        const tooltipRect = tooltipBox.getBoundingClientRect()
        let top = 0
        let left = 0
        let arrowTop = '0'
        let arrowLeft = '50%'

        switch (position) {
          case 'top':
            top = containerRect.top - tooltipRect.height - 10
            left = containerRect.left + containerRect.width / 2 - tooltipRect.width / 2
            arrowTop = '100%'
            arrowLeft = `${tooltipRect.width / 2}px`
            break

          case 'bottom':
            top = containerRect.bottom + 10
            left = containerRect.left + containerRect.width / 2 - tooltipRect.width / 2

            if (left + tooltipRect.width > window.innerWidth) {
              left = containerRect.right - tooltipRect.width
              arrowLeft = `${tooltipRect.width - containerRect.width / 2}px`
            }

            if (left < 0) {
              left = containerRect.left
              arrowLeft = `${containerRect.width / 2}px`
            }

            arrowTop = '-5px'
            break

          case 'left':
            top = containerRect.top + containerRect.height / 2 - tooltipRect.height / 2
            left = containerRect.left - tooltipRect.width - 10
            arrowTop = `${tooltipRect.height / 2}px`
            arrowLeft = '100%'
            break

          case 'right':
            top = containerRect.top + containerRect.height / 2 - tooltipRect.height / 2
            left = containerRect.right + 10

            if (left + tooltipRect.width > window.innerWidth) {
              left = containerRect.left - tooltipRect.width - 10
            }

            arrowTop = `${tooltipRect.height / 2}px`
            arrowLeft = '-1px'
            break

          default:
            break
        }

        setTooltipStyle({
          top: `${Math.max(10, Math.min(window.innerHeight - tooltipRect.height - 10, top))}px`,
          left: `${Math.max(10, left)}px`,
          ...customStyles,
        })

        setArrowStyle({
          top: arrowTop,
          left: arrowLeft,
          transform: 'translate(-50%, -50%)',
        })
      }
    }
  }, [showTooltip, position])

  return (
    <span
      ref={containerRef}
      className='tooltip-container'
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {children}
      {showTooltip && (
        <div className={`tooltip-box tooltip-${position}`} style={tooltipStyle}>
          <div className='custom-tooltip-arrow' style={arrowStyle} />
          {message}
        </div>
      )}
    </span>
  )
}

export default CustomTooltip
